<template>
  <div class="flex items-center">
    <!-- Card -->
    <ImageLinkCard
      :title="title"
      :content="content"
      :titleColor="textColor"
      :contentColor="textColor"
      :linkURL="hasModal ? '' : linkURL"
      :to="hasModal ? null : linkTo"
      :target="computedTarget"
      :elevation="1"
      :clickable="isClickable"
      :useFullPageNavigation="useFullPageNavigation"
      leftSideWidth="w-48"
      @click="toggleModal"
    >
      <template #leftSide>
        <TickCircle v-if="variant === 'complete'" color="#229150" />
        <TickCircleOutlineIcon v-else-if="variant === 'incomplete' || variant === 'newTab'" :color="greyIconColor" />
        <TimeOutlineIcon v-else-if="variant === 'pending'" :color="'#A87B00'" />
        <WarningOutlineIcon v-else-if="variant === 'warning'" :color="'#CE1B04'" />
        <LockIcon v-else-if="variant === 'locked'" :color="greyIconColor" />
        <TickCircle v-else-if="variant === 'completedTask'" color="#8A8A8F" :width="16" :height="16" />
        <div
          v-else-if="variant === 'incompleteTask'"
          class="w-16 h-16 border-2 border-solid border-grey-text-subdued rounded-full"
        />
      </template>
      <template #rightSide>
        <LockIcon
          v-if="(variant === 'pending' || variant === 'warning') && disabled"
          :color="greyIconColor"
          :width="16"
          :height="16"
        />
        <NewTabIcon v-else-if="variant === 'newTab'" :width="16" :height="16" />
        <DisclosureSmallIcon v-else-if="isClickable" />
      </template>
    </ImageLinkCard>

    <!-- Modal -->
    <component
      :is="resolvedModalComponent"
      v-if="resolvedModalComponent"
      :modelValue="showModal"
      v-bind="modalProps"
      @close="setShow(false)"
    />
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import type { RawLocation } from 'vue-router';
// Components
import Button from '../button/Button.vue';
import ImageLinkCard from '../image_link_card/ImageLinkCard.vue';
import Popup from '../popup/Popup.vue';

// Icons
import DisclosureSmallIcon from '../../graphics/icons/DisclosureSmallIcon.vue';
import LockIcon from '../../graphics/icons/LockIcon.vue';
import NewTabIcon from '../../graphics/icons/NewTabIcon.vue';
import TickCircle from '../../graphics/icons/TickCircleIcon.vue';
import TickCircleOutlineIcon from '../../graphics/icons/TickCircleOutlineIcon.vue';
import TimeOutlineIcon from '../../graphics/icons/TimeOutlineIcon.vue';
import WarningOutlineIcon from '../../graphics/icons/WarningOutlineIcon.vue';
import { markComponentRaw } from '@/helpers/markComponentRaw';

export default {
  name: 'StepCard',
  components: {
    // Components
    Button,
    ImageLinkCard,
    Popup,

    // Icons
    DisclosureSmallIcon,
    LockIcon,
    NewTabIcon,
    TickCircle,
    TickCircleOutlineIcon,
    TimeOutlineIcon,
    WarningOutlineIcon,
  },
  props: {
    variant: {
      type: String,
      default: 'complete',
      validator(value: string) {
        // The value must be one of these strings
        return ['complete', 'incomplete', 'pending', 'warning', 'locked', 'newTab', 'completedTask', 'incompleteTask'].includes(
          value
        );
      },
    },
    title: {
      type: String,
      default: '',
    },
    content: {
      type: String,
      default: '',
    },
    linkURL: {
      type: String,
      default: '',
    },
    linkTo: {
      type: [String, Object] as PropType<RawLocation>,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    modalComponent: {
      type: [Object, Function],
      default: null,
    },
    modalProps: {
      type: Object,
      default: () => ({}),
    },
    useFullPageNavigation: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      showModal: false,
    };
  },
  computed: {
    resolvedModalComponent() {
      return this.modalComponent ? markComponentRaw(this.modalComponent) : null;
    },
    computedTarget() {
      const target = this.variant === 'newTab' ? '_blank' : '_self';

      return target;
    },
    greyIconColor(): string {
      return '#8A8A8F';
    },
    textColor(): string {
      if (this.disabled) {
        return 'grey-text-subdued';
      }
      return 'grey-header';
    },
    hasModal(): boolean {
      return !!this.modalComponent;
    },
    isClickable(): boolean {
      return !this.disabled || (this.disabled && (!!this.linkURL || !!this.linkTo)) || this.hasModal;
    },
  },
  methods: {
    toggleModal() {
      if (this.hasModal) {
        this.showModal = !this.showModal;
      }
    },
    setShow(value: boolean) {
      this.showModal = value;
    },
  },
};
</script>
